export class TextTextureRenderer {
    constructor(stage, canvas, text, settings, pixelRatio) {
        this.stage = stage;
        this.canvas = canvas;
        this.text = text;
        this.settings = settings;
        this.pixelRatio = pixelRatio;
        this._context = this.canvas.getContext("2d");
    }
    setFontProperties() {
        this._context.font = this._getFontSetting();
    }
    _getFontSetting() {
        const fontWeight = this.settings.fontWeight || 400;
        const fontStyle = this.settings.fontStyle || "normal";
        const fontSize = (this.settings.fontSize || 40) * this.pixelRatio;
        const fontFaces = this._getFontFaces();
        return `${fontStyle} ${fontWeight} ${fontSize}px ${fontFaces.join(",")}`;
    }
    _getFontFaces() {
        let fontFace = this.settings.fontFace;
        if (!fontFace) {
            fontFace = this.stage.defaultFontFace;
        }
        if (fontFace) {
            return fontFace.map((fontFaceName) => {
                if (fontFaceName === "serif" || fontFaceName === "sans-serif") {
                    return fontFaceName;
                }
                else {
                    return `"${fontFaceName}"`;
                }
            });
        }
        else {
            return [];
        }
    }
    _load() {
        const documentFonts = getDocumentFonts();
        if (documentFonts) {
            const fontSetting = this._getFontSetting();
            try {
                if (!documentFonts.check(fontSetting, this.text)) {
                    // Use a promise that waits for loading.
                    return documentFonts
                        .load(fontSetting, this.text)
                        .catch((err) => {
                        // Just load the fallback font.
                        console.warn("Font load error", err, fontSetting);
                    })
                        .then(() => {
                        if (!documentFonts.check(fontSetting, this.text)) {
                            console.warn("Font not found", fontSetting);
                        }
                    });
                }
            }
            catch (e) {
                console.warn("Can't check font loading for " + fontSetting);
            }
        }
    }
    draw() {
        // We do not use a promise if possible to be able to load the texture during the current drawFrame cycle.
        const loadPromise = this._load();
        if (!loadPromise) {
            this._draw();
        }
        else {
            return loadPromise.then(() => {
                this._draw();
            });
        }
    }
    _draw() {
        const renderInfo = {};
        const pixelRatio = this.pixelRatio;
        let { fontSize = 40, cutSx = 0, cutEx = 0, cutSy = 0, cutEy = 0 } = this.settings;
        const text = this.text;
        fontSize = fontSize * pixelRatio;
        cutSx = cutSx * pixelRatio;
        cutEx = cutEx * pixelRatio;
        cutSy = cutSy * pixelRatio;
        cutEy = cutEy * pixelRatio;
        // Set font properties.
        this.setFontProperties();
        const sizeInfo = this._context.measureText(text);
        renderInfo.sizeInfo = sizeInfo;
        let width = Math.ceil(sizeInfo.width);
        let height = Math.ceil(fontSize);
        renderInfo.w = width;
        renderInfo.h = height;
        renderInfo.pixelRatio = pixelRatio;
        // To prevent canvas errors.
        if (!width)
            width = 1;
        if (!height)
            height = 1;
        if (cutSx || cutEx) {
            width = Math.min(width, cutEx - cutSx);
        }
        if (cutSy || cutEy) {
            height = Math.min(height, cutEy - cutSy);
        }
        // Add extra margin to prevent issue with clipped text when scaling.
        this.canvas.width = width;
        this.canvas.height = height;
        // Canvas context has been reset.
        this.setFontProperties();
        if (cutSx || cutSy) {
            this._context.translate(-cutSx, -cutSy);
        }
        this._context.textBaseline = "top";
        this._context.fillStyle = "white";
        this._context.fillText(text, 0, 0);
        if (cutSx || cutSy) {
            this._context.translate(cutSx, cutSy);
        }
        this.renderInfo = renderInfo;
    }
}
function getDocumentFonts() {
    return document.fonts;
}
//# sourceMappingURL=TextTextureRenderer.js.map